import { type ReactNode, useMemo } from 'react';

import type { Breadcrumb, PostHeaderData, PostTheme, TPostHeaderElement } from '@shared/dream-components';
import { PostHeaderElement } from '@shared/dream-components';

import { usePostPageContext } from '~/context/usePostPageContext';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PostLoaderData } from '~/routes/p/$slug';
import rewriteSrc from '~/utils/rewriteSrc';

interface Props {
  node: TPostHeaderElement;
  children: ReactNode | ReactNode[];
}

const PostHeader = ({ node, children }: Props) => {
  const { post, site, onlyRenderComments, isDraft } = useTypedLoaderData<PostLoaderData>();
  const { utmParams } = usePostPageContext() || {};
  const postTheme = post?.post_theme;

  const postAuthors = useMemo(() => {
    if (!Array.isArray(post?.authors) || post.authors.length === 0) {
      return [];
    }

    return post.authors.map((author) => ({
      ...author,
      profile_picture: {
        ...author.profile_picture,
        url: rewriteSrc(author.profile_picture.url),
        thumb: {
          url: rewriteSrc(author.profile_picture.thumb.url),
        },
        landscape: {
          url: rewriteSrc(author.profile_picture.landscape.url),
        },
      },
    }));
  }, [post?.authors]);

  const postData: PostHeaderData = {
    id: post?.id,
    web_title: post?.web_title,
    web_subtitle: post?.web_subtitle || '',
    comments_state: post?.comments_state,
    override_scheduled_at: isDraft ? new Date().toISOString() : post?.override_scheduled_at,
    scheduled_at: post?.scheduled_at,
    audience: post?.audience,
    is_premium: post?.is_premium,
    slug: post?.slug,
    content_tags: post?.content_tags,
    image_url: rewriteSrc(post?.image_url),
    authors: postAuthors,
    estimated_reading_time: post?.estimated_reading_time,
    estimated_reading_time_display: post?.estimated_reading_time_display,
    display_thumbnail_on_web: post?.display_thumbnail_on_web,
    author_urls: post?.author_urls,
  };

  const breadCrumbs: Breadcrumb[] = [
    {
      text: 'Home',
      href: '/',
    },
    {
      text: 'Posts',
      href: undefined,
    },
    {
      text: post?.web_title,
      href: post?.slug,
    },
  ];

  const shareUrl = typeof document !== 'undefined' ? document.location.href : '#';

  // check if we have a post and don't render if not
  if (!post || onlyRenderComments) {
    return null;
  }

  return (
    <PostHeaderElement
      postTheme={postTheme as PostTheme}
      webTheme={site.publication.web_theme}
      utmParams={utmParams}
      element={{
        ...node,
        attrs: {
          ...node.attrs,
          data: {
            post: postData,
            breadcrumbs: breadCrumbs,
            shareUrl,
          },
        },
      }}
      isLive
    >
      {children}
    </PostHeaderElement>
  );
};

export default PostHeader;
